import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { FaSave, FaTrash } from 'react-icons/fa'
import { IFeed_Input, IFeed_Input_Link, IFeed_Input_Rule, IFeed_Input_UpdateInterval_Timezone } from './types'
import FeedRulesForm from './feed-rules-form'
import FeedLinksForm from './feed-links-form'
import { getUserTimezoneOffset } from '../../../_utils'

interface FeedModalProps {
    show: boolean
    onClose: () => void
    onSave: (input: IFeed_Input) => Promise<void>
    onRemove?: () => void
    showRemove?: boolean
    feedInput?: IFeed_Input
}

const generateId = () => Math.random().toString(36).substring(2, 8)

const getInitialInput = (): IFeed_Input => ({
    name: '',
    rules: [],
    updateInterval: {
        frequency: 'daily',
        hour: '00',
        timezoneOffset: getUserTimezoneOffset(),
    },
    links: [{ name: '', link: '', id: generateId(), kind: 'csv', dataSourceType: 'url' }],
})

const FeedInputModal: React.FC<FeedModalProps> = ({ show, onClose, onSave, onRemove, showRemove, feedInput }) => {
    const [localInput, setLocalInput] = useState<IFeed_Input>(getInitialInput)
    const [formSubmitted, setFormSubmitted] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState('')
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        if (feedInput) {
            const updateInterval = feedInput.updateInterval.frequency === 'hourly'
                ? { frequency: 'hourly' as const }
                : {
                    ...feedInput.updateInterval,
                    hour: feedInput.updateInterval.hour || '00',
                    timezoneOffset: feedInput.updateInterval.timezoneOffset ?? getUserTimezoneOffset(),
                    ...(feedInput.updateInterval.frequency === 'weekly'
                        ? { dayOfWeek: feedInput.updateInterval.dayOfWeek || 'Sunday' }
                        : {}),
                    ...(feedInput.updateInterval.frequency === 'monthly'
                        ? { dayOfMonth: feedInput.updateInterval.dayOfMonth || '1' }
                        : {}),
                }

            setLocalInput({
                ...feedInput,
                updateInterval,
                rules: [...feedInput.rules],
                links: feedInput.links.length ? feedInput.links.map((link) => ({ ...link })) : getInitialInput().links,
            })
        } else {
            setLocalInput(getInitialInput())
        }
        setFormSubmitted(false)
        setSaveError('')
        setIsSaving(false)
    }, [feedInput, show])

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormSubmitted(true)
        setSaveError('')

        if (!localInput.name.trim() || !formRef.current?.checkValidity()) {
            formRef.current?.reportValidity()
            if (!localInput.name.trim()) {
                formRef.current?.querySelector<HTMLInputElement>('#feed-name')?.focus()
            }
            return
        }

        setIsSaving(true)
        try {
            await onSave({ ...localInput, name: localInput.name.trim() })
            onClose()
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'The feed could not be saved. Please try again.')
        } finally {
            setIsSaving(false)
        }
    }

    const handleRulesChange = (updatedRules: IFeed_Input_Rule[]) => {
        setLocalInput((current) => ({ ...current, rules: updatedRules }))
    }

    const handleLinksChange = (updatedLinks: IFeed_Input_Link[]) => {
        setLocalInput((current) => ({ ...current, links: updatedLinks }))
    }

    const handleAddLink = () => {
        setLocalInput((current) => ({
            ...current,
            links: [
                ...current.links,
                { name: '', link: '', id: generateId(), kind: 'csv', dataSourceType: 'url' },
            ],
        }))
    }

    const handleFrequencyChange = (frequency: IFeed_Input['updateInterval']['frequency']) => {
        if (frequency === 'hourly') {
            setLocalInput((current) => ({ ...current, updateInterval: { frequency: 'hourly' } }))
            return
        }

        setLocalInput((current) => {
            const previous = current.updateInterval
            const scheduledInterval: IFeed_Input_UpdateInterval_Timezone = {
                frequency,
                hour: 'hour' in previous ? previous.hour : '00',
                timezoneOffset: 'timezoneOffset' in previous ? previous.timezoneOffset : getUserTimezoneOffset(),
                ...(frequency === 'weekly' ? {
                    dayOfWeek: 'dayOfWeek' in previous ? previous.dayOfWeek || 'Sunday' : 'Sunday',
                } : {}),
                ...(frequency === 'monthly' ? {
                    dayOfMonth: 'dayOfMonth' in previous ? previous.dayOfMonth || '1' : '1',
                } : {}),
            }
            return { ...current, updateInterval: scheduledInterval }
        })
    }

    const timezoneOptions = Array.from({ length: 27 }, (_, index) => index - 12)
    const hours = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, '0'))
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const daysOfMonth = Array.from({ length: 31 }, (_, index) => (index + 1).toString())
    const isEditing = Boolean(showRemove)

    return (
        <Modal
            show={show}
            onHide={() => !isSaving && onClose()}
            size="xl"
            backdrop="static"
            keyboard={!isSaving}
            scrollable
            centered
            contentClassName="feed-editor-modal"
        >
            <Modal.Header closeButton={!isSaving}>
                <div>
                    <Modal.Title>{isEditing ? 'Edit feed' : 'Create feed'}</Modal.Title>
                    <div className="text-muted small mt-1">
                        Configure sources, update frequency and optional transformation rules.
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                <Form id="feed-editor-form" ref={formRef} noValidate onSubmit={handleSubmit}>
                    {saveError && <Alert variant="danger" role="alert">{saveError}</Alert>}

                    <section className="feed-editor-section" aria-labelledby="feed-details-heading">
                        <div className="feed-section-heading">
                            <span className="feed-section-step">1</span>
                            <div>
                                <h5 id="feed-details-heading" className="mb-1">Feed details</h5>
                                <p className="text-muted small mb-0">Give the feed a recognizable name and choose when it runs.</p>
                            </div>
                        </div>

                        <Form.Group controlId="feed-name" className="mb-4">
                            <Form.Label>Feed name</Form.Label>
                            <Form.Control
                                type="text"
                                value={localInput.name || ''}
                                onChange={(event) => setLocalInput((current) => ({ ...current, name: event.target.value }))}
                                placeholder="For example: Daily product export"
                                autoComplete="off"
                                required
                                isInvalid={formSubmitted && !localInput.name.trim()}
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide a feed name.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <div className="feed-schedule-panel">
                            <div className="fw-semibold mb-1">Update schedule</div>
                            <div className="text-muted small mb-3">Automatic runs use the timezone selected below.</div>
                            <Row className="g-3">
                                <Col xs={12} sm={6} lg={3}>
                                    <Form.Group controlId="feed-frequency">
                                        <Form.Label>Frequency</Form.Label>
                                        <Form.Select
                                            value={localInput.updateInterval.frequency}
                                            onChange={(event) => handleFrequencyChange(event.target.value as IFeed_Input['updateInterval']['frequency'])}
                                            required
                                        >
                                            <option value="hourly">Hourly</option>
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                {localInput.updateInterval.frequency !== 'hourly' && (
                                    <>
                                        <Col xs={12} sm={6} lg={3}>
                                            <Form.Group controlId="feed-hour">
                                                <Form.Label>Time</Form.Label>
                                                <Form.Select
                                                    value={localInput.updateInterval.hour || '00'}
                                                    onChange={(event) => setLocalInput((current) => ({
                                                        ...current,
                                                        updateInterval: {
                                                            ...current.updateInterval,
                                                            hour: event.target.value,
                                                        } as IFeed_Input_UpdateInterval_Timezone,
                                                    }))}
                                                    required
                                                >
                                                    {hours.map((hour) => <option key={hour} value={hour}>{hour}:00</option>)}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} sm={6} lg={3}>
                                            <Form.Group controlId="feed-timezone">
                                                <Form.Label>Timezone</Form.Label>
                                                <Form.Select
                                                    value={localInput.updateInterval.timezoneOffset.toString()}
                                                    onChange={(event) => setLocalInput((current) => ({
                                                        ...current,
                                                        updateInterval: {
                                                            ...current.updateInterval,
                                                            timezoneOffset: Number(event.target.value),
                                                        } as IFeed_Input_UpdateInterval_Timezone,
                                                    }))}
                                                    required
                                                >
                                                    {timezoneOptions.map((timezone) => (
                                                        <option key={timezone} value={timezone}>
                                                            GMT{timezone >= 0 ? `+${timezone}` : timezone}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        {localInput.updateInterval.frequency === 'weekly' && (
                                            <Col xs={12} sm={6} lg={3}>
                                                <Form.Group controlId="feed-weekday">
                                                    <Form.Label>Day of week</Form.Label>
                                                    <Form.Select
                                                        value={localInput.updateInterval.dayOfWeek || 'Sunday'}
                                                        onChange={(event) => setLocalInput((current) => ({
                                                            ...current,
                                                            updateInterval: {
                                                                ...current.updateInterval,
                                                                dayOfWeek: event.target.value,
                                                            } as IFeed_Input_UpdateInterval_Timezone,
                                                        }))}
                                                        required
                                                    >
                                                        {daysOfWeek.map((day) => <option key={day} value={day}>{day}</option>)}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        )}

                                        {localInput.updateInterval.frequency === 'monthly' && (
                                            <Col xs={12} sm={6} lg={3}>
                                                <Form.Group controlId="feed-monthday">
                                                    <Form.Label>Day of month</Form.Label>
                                                    <Form.Select
                                                        value={localInput.updateInterval.dayOfMonth || '1'}
                                                        onChange={(event) => setLocalInput((current) => ({
                                                            ...current,
                                                            updateInterval: {
                                                                ...current.updateInterval,
                                                                dayOfMonth: event.target.value,
                                                            } as IFeed_Input_UpdateInterval_Timezone,
                                                        }))}
                                                        required
                                                    >
                                                        {daysOfMonth.map((day) => <option key={day} value={day}>{day}</option>)}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </>
                                )}
                            </Row>
                        </div>
                    </section>

                    <FeedLinksForm
                        links={localInput.links}
                        onChange={handleLinksChange}
                        onAddLink={handleAddLink}
                    />

                    <FeedRulesForm
                        rules={localInput.rules}
                        onChange={handleRulesChange}
                    />
                </Form>
            </Modal.Body>

            <Modal.Footer className="feed-modal-footer">
                {showRemove && onRemove && (
                    <Button variant="outline-danger" onClick={onRemove} disabled={isSaving} className="me-auto">
                        <FaTrash aria-hidden="true" className="me-2" />
                        Remove feed
                    </Button>
                )}
                <Button variant="outline-secondary" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button type="submit" form="feed-editor-form" variant="primary" disabled={isSaving}>
                    {isSaving ? (
                        <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
                    ) : (
                        <><FaSave aria-hidden="true" className="me-2" />{isEditing ? 'Save changes' : 'Create feed'}</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default FeedInputModal
